import React, { createContext, useEffect, useState, useMemo } from 'react'

import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr'

import selector from '@tabeeb/modules/shared/utils/selector'
import domainService from '@tabeeb/services/domainService'

import { useDispatch, useSelector } from '@tabeeb/store'
import { getIsUserAuthorized } from '@tabeeb/modules/account/selectors'

import signalrEvents, { createTabeebHubAction } from '../events'
import { HubName } from '../types'
import {
  aiServiceHubName,
  autodeskHubName,
  healthDeviceHubName,
  spreadsheetHubName,
  tabeebHubName,
} from '../services/constants'

const routes: Record<HubName, string> = {
  [tabeebHubName]: process.env.TABEEB_API_URL!,
  [healthDeviceHubName]: `${process.env.TABEEB_API_URL}health-device-service/`,
  [spreadsheetHubName]: `${process.env.TABEEB_API_URL}spreadsheet-service/api/`,
  [autodeskHubName]: `${process.env.TABEEB_API_URL}autodesk-service/`,
  [aiServiceHubName]: `${process.env.TABEEB_API_URL}ai-service/`,
}

export interface SignalRHubContext {
  connection: HubConnection | null
  isConnected: boolean
}

const hubContexts = new Map<HubName, React.Context<SignalRHubContext>>()

export const getHubContext = (hubName: HubName) => {
  if (!hubContexts.has(hubName)) {
    const context = createContext<SignalRHubContext>({
      connection: null,
      isConnected: false,
    })

    hubContexts.set(hubName, context)
  }

  return hubContexts.get(hubName)!
}

const hubConnections = new Map<HubName, HubConnection>()

export const getHubConnection = (hubName: HubName): HubConnection | null => {
  return hubConnections.get(hubName) ?? null
}

export interface SignalRContextProviderProps {
  hubName: HubName
  enabled?: boolean
  onConnected?: (connection: HubConnection) => void
  onDisconnecting?: (connection: HubConnection) => void
}

type EventHandler = (...args: any[]) => void

const SignalRContextProvider: React.FC<SignalRContextProviderProps> = ({
  enabled = true,
  hubName,
  children,
  onConnected,
  onDisconnecting,
}) => {
  const dispatch = useDispatch()
  const isAuthenticated = useSelector(getIsUserAuthorized)

  const [connection, setConnection] = useState<HubConnection | null>(null)
  const [isConnected, setIsConnected] = useState(false)

  const HubContext = getHubContext(hubName)

  const active = enabled && isAuthenticated

  useEffect(() => {
    const hubEvents = signalrEvents[hubName]

    if (!active || !hubEvents) {
      return () => {}
    }

    const hubConnection = new HubConnectionBuilder()
      .withUrl(`${routes[hubName]}${hubName}`, {
        accessTokenFactory: () => selector.getAccessToken(),
        headers: { 'X-Tenant-Subdomain': domainService.subdomain },
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Critical)
      .build()

    const handlers: { eventName: string; handler: EventHandler }[] = []

    Object.entries(hubEvents).forEach(([signalrEvent, action]: [string, unknown]) => {
      const handler: EventHandler = (...args) => {
        dispatch((action as ReturnType<typeof createTabeebHubAction>)(args))
      }

      handlers.push({ eventName: signalrEvent, handler })
    })

    for (const { eventName, handler } of handlers) {
      hubConnection.on(eventName, handler)
    }

    hubConnection.onreconnected((connectionId) => {
      if (signalrEvents[hubName].onReconnected) {
        dispatch(signalrEvents[hubName].onReconnected({ connectionId }))
      }
    })

    hubConnection.onclose((connectionId) => {
      if (signalrEvents[hubName].onClose) {
        dispatch(signalrEvents[hubName].onClose({ connectionId }))
      }
    })

    setConnection(hubConnection)

    hubConnections.set(hubName, hubConnection)

    hubConnection
      .start()
      .then(() => {
        onConnected?.(hubConnection)
        setIsConnected(true)
      })
      // eslint-disable-next-line no-console
      .catch(console.error)

    return () => {
      onDisconnecting?.(hubConnection)

      for (const { eventName, handler } of handlers) {
        hubConnection.off(eventName, handler)
      }

      // eslint-disable-next-line no-console
      hubConnection.stop().catch(console.error)
      hubConnections.delete(hubName)
    }
  }, [hubName, active, dispatch, onConnected, onDisconnecting])

  const value = useMemo(() => ({ connection, isConnected }), [connection, isConnected])

  return <HubContext.Provider value={value}>{children}</HubContext.Provider>
}

export default SignalRContextProvider
